/**
 * Cross-environment diagnostic breadcrumbs from the browser.
 *
 * WHY: production browser behaviour was invisible from the preview pod, so
 * failures that live entirely in the client (audio blocked by autoplay policy,
 * a ceremony promise chain that never resolves, a turn gate reading the wrong
 * id) could only be guessed at. These post to /api/diag/events, which writes to
 * the database preview can read.
 *
 * RULES
 *   • Never throw, never block, never retry aggressively. A failed breadcrumb
 *     is worth nothing and must cost nothing.
 *   • Batched on a short timer so a burst (every countdown tick) is one request.
 *   • Session-scoped id so two tabs of the same user are distinguishable — the
 *     commissioner tab vs the board tab is exactly the distinction that matters.
 */
const BACKEND = process.env.REACT_APP_BACKEND_URL;
const FLUSH_MS = 1500;
const MAX_QUEUE = 40;

const sessionId = (() => {
  try {
    const k = "pgf_diag_session";
    let v = sessionStorage.getItem(k);
    if (!v) {
      v = `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
      sessionStorage.setItem(k, v);
    }
    return v;
  } catch {
    return "no-storage";
  }
})();

let queue = [];
let timer = null;
let context = { league_id: null, user_id: null };

export const setDiagContext = (next) => {
  context = { ...context, ...next };
};

const flush = () => {
  timer = null;
  if (!queue.length || !BACKEND) return;
  const events = queue;
  queue = [];
  try {
    fetch(`${BACKEND}/api/diag/events`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        events,
        // Stated by the browser itself so the ingress cannot blur which
        // deployment these came from.
        env: typeof window !== "undefined" ? window.location.host : "unknown",
      }),
      keepalive: true, // survives the tab navigating away mid-ceremony
    }).catch(() => {});
  } catch {
    /* diagnostics must never surface */
  }
};

export const diag = (event, detail, level = "info") => {
  try {
    if (queue.length >= MAX_QUEUE) return;
    queue.push({
      event,
      level,
      detail: detail === undefined ? null : detail,
      session_id: sessionId,
      league_id: context.league_id || null,
      user_id: context.user_id || null,
    });
    if (!timer) timer = setTimeout(flush, FLUSH_MS);
  } catch {
    /* ignore */
  }
};

export const diagError = (event, detail) => diag(event, detail, "error");

/** Flush immediately — for beats that precede a navigation or unload. */
export const diagFlush = () => {
  if (timer) clearTimeout(timer);
  flush();
};

/** Global crash capture. A minified production build turns an uncaught error
 *  into a silent dead UI, which is exactly what "nobody could click" looks
 *  like. Installed once from index.js. */
export const installGlobalDiagHandlers = () => {
  if (typeof window === "undefined" || window.__pgfDiagInstalled) return;
  window.__pgfDiagInstalled = true;

  window.addEventListener("error", (e) => {
    diagError("js.uncaught_error", {
      message: String(e?.message || ""),
      source: `${e?.filename || ""}:${e?.lineno || 0}:${e?.colno || 0}`,
      stack: String(e?.error?.stack || "").slice(0, 600),
    });
  });

  window.addEventListener("unhandledrejection", (e) => {
    const r = e?.reason;
    diagError("js.unhandled_rejection", {
      message: String(r?.message || r || ""),
      stack: String(r?.stack || "").slice(0, 600),
    });
  });

  window.addEventListener("pagehide", diagFlush);
  diag("app.boot", {
    href: String(window.location?.href || "").slice(0, 200),
    ua: String(navigator?.userAgent || "").slice(0, 160),
  });
};

export const diagSessionId = sessionId;
